'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Skeleton } from '@/components/ui/skeleton';
import { getBrandedImageUrl, getOptimizedImageUrl } from '@/lib/utils/image-utils';

interface EquipmentGalleryProps {
  primaryImage?: string | null;
  galleryImages: string[];
  name: string;
  isLoading?: boolean;
}

export function EquipmentGallery({ 
  primaryImage, 
  galleryImages, 
  name,
  isLoading = false
}: EquipmentGalleryProps) {
  // Convert all images to branded URLs
  const brandedPrimaryImage = getBrandedImageUrl(primaryImage);
  const brandedGalleryImages = galleryImages.map(img => getBrandedImageUrl(img)).filter(Boolean) as string[];
  const allBrandedImages = brandedPrimaryImage ? [brandedPrimaryImage, ...brandedGalleryImages] : brandedGalleryImages;
  
  const [selectedImage, setSelectedImage] = useState(brandedPrimaryImage || brandedGalleryImages[0] || '');
  
  // Get optimized URLs for display
  const mainImageUrl = selectedImage ? getOptimizedImageUrl(selectedImage, {
    width: 800,
    height: 450,
    quality: 90,
    format: 'auto',
    crop: 'fill'
  }) : null;
  
  const getThumbnailUrl = (imageUrl: string) => getOptimizedImageUrl(imageUrl, {
    width: 64,
    height: 64,
    quality: 75,
    format: 'auto',
    crop: 'fill'
  });
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="relative aspect-video overflow-hidden rounded-lg">
          <Skeleton className="h-full w-full" />
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-16 rounded-md flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="relative aspect-video overflow-hidden rounded-lg">
        {mainImageUrl ? (
          <Image
            src={mainImageUrl}
            alt={name}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 800px"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            No image available
          </div>
        )}
      </div>
      
      {allBrandedImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allBrandedImages.map((image, index) => {
            const thumbnailUrl = getThumbnailUrl(image);
            return (
              <button
                key={index}
                onClick={() => setSelectedImage(image)}
                className={`relative h-16 w-16 overflow-hidden rounded-md flex-shrink-0 ${
                  image === selectedImage ? 'ring-2 ring-primary' : ''
                }`}
              >
                {thumbnailUrl && (
                  <Image
                    src={thumbnailUrl}
                    alt={`${name} - Image ${index + 1}`}
                    fill
                    className="object-cover"
                    sizes="64px"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
